import { useTranslations } from 'next-intl';

import { cn } from '@/lib/utils';
import type { TranslateFunction } from '@/types/general';
import { getFlagUrl, getLanguageDisplay } from '@/utils/language-helpers';

import { Image } from '../ui/image';

type FlagByLanguageProps = {
  code: string;
  showLabel?: boolean;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  rounded?: boolean;
};

const SIZE_PX = {
  sm: 14,
  md: 16,
  lg: 20,
} as const;

const FlagByLanguage: React.FC<FlagByLanguageProps> = ({
  code,
  showLabel = true,
  className = '',
  size = 'md',
  rounded = false,
}) => {
  const translate = useTranslations();

  if (code === undefined || code === null || code === '') {
    return null;
  }

  const dimension = SIZE_PX[size];
  const label = getLanguageDisplay(code, translate as TranslateFunction);

  const flag = (
    <Image
      src={getFlagUrl(code)}
      alt={label}
      width={dimension}
      height={dimension}
      showSkeleton={false}
      showBrokenImage={false}
      className={cn(
        'inline-flex shrink-0 object-cover',
        rounded === true ? 'rounded-full' : 'rounded-sm',
      )}
      style={{ width: dimension, height: dimension }}
    />
  );

  if (showLabel === false) {
    return (
      <span className={cn('inline-flex shrink-0', className)}>
        {flag}
      </span>
    );
  }

  return (
    <span className={cn('inline-flex shrink-0 items-center gap-2', className)}>
      {flag}
      <span className="truncate">{label}</span>
    </span>
  );
};

export default FlagByLanguage;
